// @flow

import React from 'react';
import type { Node } from 'react';
import find from 'lodash/find';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Button, Heading } from '@riseart/common';
import { Section, SectionContent, Wrapper } from '@riseart/layout';
import { CardSimple } from '@riseart/card';
import { TextColumns } from '@riseart/cms';
import { HorizontalSlider } from '@riseart/slider';
// Keep the order of the layouts/Default and @riseart/grid imports
// one after the other to avoid conflicting order between css warning on build step
import { useQuery } from 'shared_services/apollo/useQuery';
import { useUrlParams } from 'shared_data/providers/url/useUrlParams';
import { LayoutDefault } from 'shared_components/layouts/Default';
import { GridSquare, GridSquareItem } from '@riseart/grid';
import { quiz as QUIZ_ENUM } from 'Enum';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { META_SUBSCRIBER_NAME } from 'shared_services/riseart/meta/Meta';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { useStoreCode } from 'shared_services/redux/hooks/useStoreCode';
import { extractImageFileFromData } from 'shared_services/riseart/utils/ImageUtils';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';
import {
  generateSrcForCDN,
  PicturePlaceholder,
} from 'shared_components/common/artdirection/picture/Picture';
import { ProfileInfo } from 'shared_components/quiz/common/profileinfo/ProfileInfo';
import { Score } from 'shared_components/quiz/common/score/Score';
import READ_QUIZ_PROFILE_QUERY from 'shared_data/queries/quiz/readProfile.graphql';
import LIST_COLLECTIONS_QUERY from 'shared_data/queries/quiz/listProfileCollections.graphql';
import LIST_ARTISTS_QUERY from 'shared_data/queries/quiz/listProfileArtists.graphql';
import FILTER_PROFILES_QUERY from 'shared_data/queries/quiz/filterProfiles.graphql';
import { quizSectionCenteredCls } from 'shared_components/quiz/Quiz.less';

/**
 * QuizProfile
 *
 * @param {Props} props
 */
export default function QuizProfile(): Node {
  const { formatMessage } = useIntl();
  const { slug: slugParam } = useParams();
  const store = useStoreCode();
  const { translatedLocation: location } = useUrlParams();

  // Get quiz profile
  const { data, loading, error } = useQuery(READ_QUIZ_PROFILE_QUERY, {
    variables: { id: slugParam },
    context: { customOptions: { errorSuppressFromResponse: true } },
  });
  const { readQuizProfile } = data || {};
  const hasError = !!error || !readQuizProfile;
  const {
    slug,
    type,
    name,
    images,
    longDescription,
    values,
    pageTitle,
    metaTitle,
    metaDescription,
    metaKeywords,
    locales,
  } = readQuizProfile || {};
  const quizStartUrl = UrlAssembler.byRouteKey('quizStart');
  const hrefLangParams = locales ? generateHrefLangParams(locales) : null;

  // Get collections list
  const { data: listCollectionsData } = useQuery(LIST_COLLECTIONS_QUERY, {
    ssr: false,
    variables: { profileId: slugParam, store, items: 3 },
    skip: !store,
  });
  const collectionsItems =
    (listCollectionsData &&
      listCollectionsData.listQuizProfileCollections &&
      listCollectionsData.listQuizProfileCollections.items) ||
    [];

  // Get artists list
  const { data: listArtistsData } = useQuery(LIST_ARTISTS_QUERY, {
    ssr: false,
    variables: { profileId: slugParam, items: 8 },
  });
  const artistsList =
    (listArtistsData &&
      listArtistsData.listQuizProfileArtists &&
      listArtistsData.listQuizProfileArtists.items) ||
    [];

  // Filter profile items
  const { data: filterProfileData } = useQuery(FILTER_PROFILES_QUERY, {
    ssr: false,
    variables: { type, ignore: slug, page: 1, items: 8 },
  });
  const profileItems =
    (filterProfileData &&
      filterProfileData.filterQuizProfiles &&
      filterProfileData.filterQuizProfiles.items) ||
    [];

  return (
    <LayoutDefault hasFooter hasHeader scrollTopOnPathChange>
      {loading ? <IsomorphicRipple isFullScreen isActive={loading} /> : null}
      {!loading && readQuizProfile ? (
        <React.Fragment>
          <MetaProvider
            metaSubscriber={META_SUBSCRIBER_NAME.QUIZ_PROFILE_META}
            meta={{
              pageTitle:
                pageTitle || formatMessage({ id: 'meta.quizProfile.dynamicPageTitle' }, { name }),
              metaTitle:
                metaTitle || formatMessage({ id: 'meta.quizProfile.dynamicMetaTitle' }, { name }),
              metaDescription:
                metaDescription ||
                formatMessage({ id: 'meta.quizProfile.dynamicMetaDescription' }, { name }),
              metaKeywords,
              ogTitle: formatMessage(
                { id: 'components.share.messages.quiz_personality.facebook' },
                { name },
              ),
              ogDescription: metaDescription,
              ogImage: generateSrcForCDN(
                find(images, { type: QUIZ_ENUM.profile.image.type.TYPE_SHARE }),
              ),
              linkHreflang: hrefLangParams,
              linkHreflangRedirect: hrefLangParams,
              linkCanonical: UrlAssembler.quizProfile(slug, {
                origin: LocationManager.get('origin'),
              }),
              linkCanonicalRedirect: UrlAssembler.quizProfile(slug, {
                ...(location.search ? { search: location.search } : null),
                ...(location.hash ? { hash: location.hash } : null),
              }),
            }}
            hasError={hasError}
          />
          <Section>
            <Wrapper sizes="narrow">
              <ProfileInfo
                title={name}
                subtitle={formatMessage({ id: 'components.quiz.art_personality' })}
                framed
              >
                <Button type="primary" size="medium" isAnchor href={quizStartUrl}>
                  {formatMessage({ id: 'components.quiz.discover_your_personality' })}
                </Button>
                <TextColumns
                  // copy the original array so that sort does not mutate it
                  columns={[...longDescription].sort((a, b) => a.column - b.column)}
                />
              </ProfileInfo>
            </Wrapper>
          </Section>
          <Section background="gray">
            <Wrapper className={quizSectionCenteredCls}>
              <SectionContent
                title={formatMessage(
                  { id: 'components.quiz.profile_compared_to_others' },
                  { name },
                )}
                headingProps={{ align: 'center' }}
              >
                <Score values={values} />
                <Button type="primary" size="medium" isAnchor href={quizStartUrl}>
                  {formatMessage({ id: 'components.quiz.discover_your_personality' })}
                </Button>
              </SectionContent>
            </Wrapper>
          </Section>
          {collectionsItems.length ? (
            <Section>
              <Wrapper
                sizes={{
                  xs: 'wide',
                  sm: 'wide',
                  md: 'wide',
                  lg: 'narrow',
                  xl: 'narrow',
                }}
              >
                <SectionContent
                  title={formatMessage({ id: 'components.quiz.result_collection_title' }, { name })}
                  headingProps={{ align: 'center' }}
                >
                  <GridSquare columnCount={3}>
                    {collectionsItems.map((item) => (
                      <GridSquareItem key={item.id} columnCount={3}>
                        <CardSimple
                          key={item.id}
                          title={item.name}
                          link={{ to: UrlAssembler.collectionDetail(item.id, item.slug) }}
                        >
                          <PicturePlaceholder
                            image={extractImageFileFromData.collectionImage(
                              item.images,
                              'TYPE_MAIN_SQUARE',
                            )}
                            alt={item.name}
                            artDirectionKey="collection.square"
                            type="grid.tilesLarge"
                            lazyload
                          />
                        </CardSimple>
                      </GridSquareItem>
                    ))}
                  </GridSquare>
                </SectionContent>
              </Wrapper>
            </Section>
          ) : null}
          {artistsList.length ? (
            <Section>
              <Wrapper
                sizes={{
                  xs: 'wide',
                  sm: 'wide',
                  md: 'wide',
                  lg: 'narrow',
                  xl: 'narrow',
                }}
              >
                <SectionContent
                  title={formatMessage({ id: 'components.quiz.result_artists_title' }, { name })}
                  headingProps={{ align: 'center' }}
                >
                  <HorizontalSlider columnCount="4">
                    {artistsList.map((item) => (
                      <CardSimple
                        key={item.id}
                        title={item.name}
                        link={{ to: UrlAssembler.artistProfile(item.id, item.alias) }}
                      >
                        <PicturePlaceholder
                          image={extractImageFileFromData.quizProfileRelationImage(item.images)}
                          alt={item.name}
                          artDirectionKey="quizProfileRelation.main"
                          type="slider.gridLarge"
                          lazyload
                        />
                      </CardSimple>
                    ))}
                  </HorizontalSlider>
                </SectionContent>
              </Wrapper>
            </Section>
          ) : null}
          {profileItems.length ? (
            <Section>
              <Wrapper
                sizes={{
                  xs: 'wide',
                  sm: 'wide',
                  md: 'wide',
                  lg: 'narrow',
                  xl: 'narrow',
                }}
              >
                <SectionContent
                  title={formatMessage({ id: 'components.quiz.other_art_personalities' }, { name })}
                  headingProps={{ align: 'center' }}
                >
                  <HorizontalSlider columnCount="4">
                    {profileItems.map((item) => (
                      <CardSimple
                        key={item.id}
                        title={item.name}
                        link={{ to: UrlAssembler.quizProfile(item.slug) }}
                      >
                        <PicturePlaceholder
                          image={extractImageFileFromData.quizProfileImage(item.images)}
                          alt={item.name}
                          artDirectionKey="quizProfile.main"
                          type="slider.gridLarge"
                          lazyload
                        />
                      </CardSimple>
                    ))}
                  </HorizontalSlider>
                </SectionContent>
              </Wrapper>
            </Section>
          ) : null}
          <Section>
            <Wrapper className={quizSectionCenteredCls}>
              <SectionContent
                title={formatMessage({
                  id: 'components.quiz.discover_your_personality',
                })}
                headingProps={{ align: 'center' }}
              >
                <Heading tag="h4" level="4" align="center">
                  {formatMessage({
                    id: 'components.quiz.discover_visual_preferences',
                  })}
                </Heading>
                <Button type="primary" isAnchor href={quizStartUrl}>
                  {formatMessage({ id: 'components.quiz.take_test' })}
                </Button>
              </SectionContent>
            </Wrapper>
          </Section>
        </React.Fragment>
      ) : null}
    </LayoutDefault>
  );
}
